/**
 * @file env.ts
 * @brief Centralized, type-safe access to Vite environment variables.
 *
 * @details
 * - Vite exposes only variables prefixed with `VITE_*` to the browser
 *   bundle. This module reads them once at module-load time and exports
 *   a typed `env` object so feature code never touches `import.meta.env`
 *   directly.
 * - Anything that needs to be overridable across deploys (API base URL,
 *   app name, environment label) lives here.
 *
 * @dependencies
 * - Vite's `import.meta.env` runtime
 *
 * @references
 * - WEBPANEL/.env.example         (variable list)
 * - WEBPANEL/src/shared/api/client.ts (consumer)
 *
 * @author DES
 * @date 26-05-2026
 */

// ----------------------------------------------------------------------
// Read raw values once at module load
// In dev `VITE_API_BASE_URL` is usually empty so the SPA hits same-origin
// `/api/...` paths, which Vite then proxies to the backend (port 4000).
// In production we typically deploy behind Nginx that reverse-proxies
// `/api` to the backend, so empty string still works.
// ----------------------------------------------------------------------
const rawBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

export const env = {
  apiBaseUrl: rawBaseUrl.replace(/\/$/, ""),
  appName: import.meta.env.VITE_APP_NAME ?? "Verblizr Control Panel",
  appEnv: import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE ?? "development",
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "",
  /** Same public Web OAuth client the phone uses. */
  googleWebClientId:
    import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID ??
    "683921787260-eaukrub1v0oalof1llvn8sg4h9sgs532.apps.googleusercontent.com",
  /** Apple Services ID for the web popup. Empty until it is created. */
  appleServicesId:
    import.meta.env.VITE_APPLE_SERVICES_ID ?? "mobile.verblizr.web",
  /**
   * Sign in with Microsoft (06-10-2026): the Azure app registration's
   * Application (client) ID. Empty = no Microsoft button.
   */
  microsoftClientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID ?? "",
  /**
   * Live interpretation on web + desktop is a pilot (DES 01-10-2026). Comma
   * list of company ids that see it, or "*" for everyone. Unset: everyone in
   * dev, nobody in prod. See features/interpret/pilot.ts.
   */
  webInterpretCompanies:
    import.meta.env.VITE_WEB_INTERPRET_COMPANY_IDS ?? (import.meta.env.DEV ? "*" : ""),
  /**
   * VAT wording beside prices on Billing (17-10-2026): "incl" → "incl. VAT",
   * unset → no label (keep off; prod TAX_RATE is 0). No "+VAT" mode: not VAT
   * registered (night E2E F3, 21-10-2026).
   */
  priceVatLabel: import.meta.env.VITE_PRICE_VAT_LABEL ?? "",
  /**
   * "Coming soon" lists on company / personal pages (Company settings,
   * Reports). Off unless VITE_SHOW_COMING_SOON=true (DES 21-10-2026): show
   * only what works. Super Admin pages ignore it.
   */
  showComingSoon: import.meta.env.VITE_SHOW_COMING_SOON === "true",
  /**
   * Company settings → Security → "Require two-step verification for all
   * members" (B31). Hidden unless VITE_COMPANY_REQUIRE_2FA=true: phones
   * without the P12 build cannot sign in to a company that requires it, so it
   * stays off until P12 is in the stores (B31 rollout step 6).
   */
  companyRequire2fa: import.meta.env.VITE_COMPANY_REQUIRE_2FA === "true",
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;

export type AppEnv = typeof env;
